import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import support from "@sonic/Assets/Icons/support";
import truck from "@sonic/Assets/Icons/truck";
import warrant from "@sonic/Assets/Icons/warrant";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const iconMap = {
    warrant,
    support,
    truck,
    none: undefined,
} as const;

const enum fields {
    title = "title",
    titleSize = "titleSize",
    subTitle = "subTitle",
    iconSize = "iconSize",
    iconImage = "iconImage",
    iconAdress = "iconAdress",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleSize]: number;
        [fields.subTitle]: string;
        [fields.iconImage]: string;
        [fields.iconSize]: number;
        [fields.iconAdress]: keyof typeof iconMap;
    };
}

export interface UniqueSellingPointStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    wrapperTitle?: InjectableCss;
    title?: TypographyProps;
    subTitle?: TypographyProps;
    image?: LazyImageProps;
}

export const uniqueSellingPointStyles: UniqueSellingPointStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: 10px;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        flex-direction: row;
                        text-align: left;
                    `,
                    css`
                        flex-direction: row;
                        text-align: left;
                    `,
                ])}
        `,
    },
    wrapper: {
        css: css``,
    },
    image: {
        width: "50px",
        height: "50px",
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    title: {
        size: fields.titleSize,
        fontFamily: "Pancetta W05 SemiBold",
        italic: true,
        css: css`
            text-transform: uppercase;
            white-space: nowrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 18px;
                    `,
                    css`
                        font-size: 18px;
                    `,
                ])};
        `,
    },
    subTitle: {
        size: 12,
        color: "#00000099",
    },
    wrapperTitle: {
        css: css`
            display: flex;
            align-items: center;
            height: 70px;
            max-width: 200px;
            flex-wrap: wrap;
        `,
    },
};

const styles = uniqueSellingPointStyles;

const UniqueSellingPoint: React.FC<OwnProps> = ({ id, fields }) => {
    const containerStyle = {
        css: css`
            ${styles.container?.css || ""}
        `,
    };
    const iconAdress = iconMap[fields.iconAdress || "none"];

    return (
        <StyledWrapper {...containerStyle}>
            <StyledWrapper {...styles.wrapper}>
                {iconAdress ? (
                    <Icon size={fields.iconSize} src={iconAdress} />
                ) : (
                    <LazyImage src={fields.iconImage} altText={fields.iconImage} {...styles.image} />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.wrapperTitle}>
                <Typography {...styles.title}>{parse(fields.title, parserOptions)}</Typography>
                {fields.subTitle && (
                    <Typography {...styles.subTitle}>{parse(fields.subTitle, parserOptions)}</Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: UniqueSellingPoint,
    definition: {
        group: "Common",
        icon: "UniqueSellingPoint",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.titleSize,
                editorTemplate: "IntegerField",
                defaultValue: 16,
            },
            {
                fieldType: "General",
                name: fields.subTitle,
                editorTemplate: "TextField",
                displayName: "subTitle",
                defaultValue: "",
            },
            {
                name: fields.iconImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: fields.iconSize,
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                isRequired: false,
            },
            {
                name: fields.iconAdress,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Support",
                        value: "support",
                    },
                    {
                        displayName: "Delivery",
                        value: "truck",
                    },
                    {
                        displayName: "Warranty",
                        value: "warrant",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 9,
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
